import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { useViewMode } from "@/lib/ViewModeContext";
import PaintUrgencyPill from "@/components/paint/PaintUrgencyPill";
import { paintApi, paintUrgency } from "@/lib/paintApi";
import { BACKEND_BASE } from "@/lib/apiClient";
import { PaintBrush, Package, Timer, Storefront, Buildings, CaretRight, WarningCircle, Info } from "@phosphor-icons/react";

/**
 * Read-only paint dashboard. All send/receive actions happen on the mobile
 * scanner — this page just surfaces the live paint queue + pending batches
 * so warehouse admins can spot bottlenecks and stale items at a glance.
 */
export default function Paint() {
  const { viewMode } = useViewMode() || {};
  const [queue, setQueue] = useState({ entries: [], thresholds: { green_days: 3, yellow_days: 7 } });
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null); // { mode, url, status, entriesLen }

  const load = useCallback(async () => {
    setError(null);
    const fetchMode = typeof window !== "undefined" ? window.__cdActiveMode : null;
    const url = `${BACKEND_BASE}/api/paint/queue`;
    // Fetch both independently so a batches failure doesn't mask a working
    // queue (and vice versa). Errors are surfaced to the UI instead of being
    // silently converted to "empty" — the user asked why the paint page
    // looked empty in test mode; surfacing the underlying reason is the
    // right fix rather than smoothing it over.
    const [qRes, bRes] = await Promise.allSettled([
      paintApi.queue(),
      paintApi.batches(),
    ]);
    if (qRes.status === "fulfilled") {
      const v = qRes.value || { entries: [], thresholds: { green_days: 3, yellow_days: 7 } };
      setQueue(v);
      setLastFetch({ mode: fetchMode, url, status: 200, entriesLen: (v.entries || []).length });
    } else {
      const err = qRes.reason;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      setError(`Paint queue failed to load — ${status ? `HTTP ${status}` : ""} ${detail}`.trim());
      setQueue({ entries: [], thresholds: { green_days: 3, yellow_days: 7 } });
      setLastFetch({ mode: fetchMode, url, status: status || "error", entriesLen: 0 });
    }
    if (bRes.status === "fulfilled") {
      setBatches((bRes.value && bRes.value.batches) || []);
    } else {
      setBatches([]);
    }
    setLoading(false);
  }, []);

  const { refresh, refreshing, lastUpdatedAt, livePolling, setLivePolling } = useAutoRefresh(load, { initialPolling: true, intervalMs: 45000 });

  // Re-fetch when the user flips the Live/Test toggle in the sidebar. Without
  // this the page keeps showing the previous mode's data until the 45s poll
  // fires or the user clicks Refresh manually.
  useEffect(() => {
    if (viewMode) refresh({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const entries = queue.entries || [];
  const thresholds = queue.thresholds || { green_days: 3, yellow_days: 7 };

  const stats = useMemo(() => {
    const unitsOut = entries.reduce((sum, e) => sum + Math.max(0, (Number(e.units_out) || 0) - (Number(e.units_returned) || 0)), 0);
    const oldest = entries.reduce((max, e) => Math.max(max, Number(e.days_out) || 0), 0);
    const overdue = entries.filter((e) => paintUrgency(e.days_out, thresholds) === "red").length;
    return { unitsOut, oldest, overdue, batchesPending: batches.filter((b) => b.status !== "received").length };
  }, [entries, thresholds, batches]);

  return (
    <div className="space-y-6" data-testid="paint-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Read-only · Scanner-driven</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Paint</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
            Live queue of items sent to paint. Send/receive actions happen on the mobile scanner.
          </p>
        </div>
        <RefreshControl
          onRefresh={refresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          livePolling={livePolling}
          onToggleLive={setLivePolling}
          testId="paint-refresh"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Units at painter" value={stats.unitsOut} icon={PaintBrush} accent="#7C3AED" />
        <Kpi label="Oldest days out" value={stats.oldest ? `${stats.oldest}d` : "—"} icon={Timer} accent="#EA580C" />
        <Kpi label="Overdue items" value={stats.overdue} icon={Package} accent="#C8102E" />
        <Kpi label="Pending batches" value={stats.batchesPending} icon={Buildings} accent="#3B82F6" />
      </div>

      {error && (
        <Card
          data-testid="paint-error"
          className="p-4 border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 flex items-start gap-3"
        >
          <WarningCircle size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-red-900 dark:text-red-200">
            <div className="font-bold uppercase tracking-wider text-xs mb-1">Paint API error</div>
            <div className="font-mono">{error}</div>
            <div className="text-xs mt-2 text-red-700 dark:text-red-300">
              Mode: <span className="font-bold uppercase">{viewMode || "unknown"}</span>. If this is expected (test namespace not seeded), ignore. Otherwise check the backend paint endpoints.
            </div>
          </div>
        </Card>
      )}

      {!error && !loading && entries.length === 0 && lastFetch && (
        <Card
          data-testid="paint-empty-debug"
          className="p-4 border-zinc-300 bg-zinc-50 dark:bg-zinc-900/50 dark:border-zinc-700 flex items-start gap-3"
        >
          <Info size={20} weight="fill" className="text-zinc-500 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs font-mono text-zinc-700 dark:text-zinc-300 leading-relaxed">
            <div className="font-bold uppercase tracking-widest text-[10px] mb-1 font-sans">Debug — paint queue is empty</div>
            <div>Backend: <span className="text-zinc-900 dark:text-zinc-100">{lastFetch.url}</span></div>
            <div>Mode header sent: <span className="text-zinc-900 dark:text-zinc-100">X-CD-Mode: {lastFetch.mode || "(none)"}</span></div>
            <div>Response: <span className="text-zinc-900 dark:text-zinc-100">HTTP {lastFetch.status} · {lastFetch.entriesLen} entries</span></div>
            <div className="mt-2 text-[11px] font-sans text-zinc-600 dark:text-zinc-400">
              If items exist elsewhere (e.g. mobile scanner shows them), they may be on a different backend namespace. Run this in a terminal to compare namespaces:
              <pre className="mt-1 text-[10px] p-2 rounded bg-zinc-100 dark:bg-zinc-800 overflow-x-auto whitespace-pre">
{`curl -H "Authorization: Bearer <TOKEN>" \\
     -H "X-CD-Mode: ${lastFetch.mode === "test" ? "test" : "live"}" \\
     ${lastFetch.url}`}
              </pre>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <SectionHeader title="Paint queue" hint={`Green ≤ ${thresholds.green_days}d · Yellow ≤ ${thresholds.yellow_days}d · Red older`} />
          {loading ? (
            <Card className="p-10 text-center text-zinc-500">Loading paint queue…</Card>
          ) : entries.length === 0 ? (
            <Card className="p-10 text-center text-zinc-500" data-testid="paint-queue-empty">Nothing at the painter right now.</Card>
          ) : (
            <Card className="border-zinc-200 overflow-hidden">
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {entries.map((e) => (
                  <QueueRow key={e.item_id} entry={e} thresholds={thresholds} />
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div className="xl:col-span-1">
          <SectionHeader title="Pending batches" />
          {loading ? (
            <Card className="p-6 text-center text-zinc-500">Loading…</Card>
          ) : batches.length === 0 ? (
            <Card className="p-6 text-center text-zinc-500" data-testid="paint-batches-empty">No pending batches.</Card>
          ) : (
            <div className="space-y-3">
              {batches.map((b) => <BatchCard key={b.id} batch={b} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-5" data-testid={`paint-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 text-3xl font-heading font-black tabular-nums">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Icon size={22} weight="bold" />
        </div>
      </div>
    </Card>
  );
}

function SectionHeader({ title, hint }) {
  return (
    <div className="flex items-baseline justify-between mb-3">
      <h2 className="font-heading font-bold text-xl">{title}</h2>
      {hint && <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{hint}</span>}
    </div>
  );
}

function QueueRow({ entry, thresholds }) {
  const atPainter = Math.max(0, (Number(entry.units_out) || 0) - (Number(entry.units_returned) || 0));
  const vendor = entry.vendor_type === "in_house" ? "In-house" : "External";
  return (
    <li>
      <Link
        to={`/orders/${entry.order_id}`}
        data-testid={`paint-queue-row-${entry.sku}`}
        className="flex items-center gap-4 px-5 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
      >
        <PaintUrgencyPill daysOut={entry.days_out} thresholds={thresholds} testId={`paint-urgency-${entry.sku}`} />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-base truncate">
            {entry.name}
            <span className="ml-2 font-mono text-xs text-zinc-500">{entry.sku}</span>
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
            {entry.order_number} · {entry.customer_name}
            {entry.color && <> · <span className="text-zinc-700 dark:text-zinc-300">{entry.color}</span></>}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              {entry.vendor_type === "in_house" ? <Buildings size={11} weight="bold" /> : <Storefront size={11} weight="bold" />}
              {vendor}
            </span>
            {entry.vendor_notes && <span className="normal-case tracking-normal text-zinc-600 dark:text-zinc-400 italic">"{entry.vendor_notes}"</span>}
            {entry.paint_batch_number && (
              <span className="font-mono normal-case tracking-normal text-zinc-500">· {entry.paint_batch_number}</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-black text-2xl tabular-nums text-purple-700 dark:text-purple-300">{atPainter}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">units</div>
        </div>
        <CaretRight size={14} className="text-zinc-300 shrink-0" weight="bold" />
      </Link>
    </li>
  );
}

function BatchCard({ batch }) {
  const total = (batch.items || []).reduce((sum, i) => sum + (Number(i.units) || 0), 0);
  return (
    <Card className="p-4" data-testid={`paint-batch-${batch.batch_number || batch.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{batch.status || "pending"}</div>
          <div className="font-heading font-black text-base truncate">{batch.batch_number || batch.id}</div>
          {batch.vendor_notes && <div className="text-xs text-zinc-500 truncate italic">"{batch.vendor_notes}"</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-black text-lg tabular-nums">{(batch.items || []).length}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">items</div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
        <span className="inline-flex items-center gap-1">
          {batch.vendor_type === "in_house" ? <Buildings size={11} weight="bold" /> : <Storefront size={11} weight="bold" />}
          {batch.vendor_type === "in_house" ? "In-house" : "External"}
        </span>
        <span className="normal-case tracking-normal text-zinc-500">
          {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : ""} · {total} units
        </span>
      </div>
    </Card>
  );
}
